// Text helpers. `sanitizeGeneratedText` enforces the product rule: no emojis and no em / en dashes in any text the
// platform generates (AI replies, templates, notifications).

const EN_DASH = String.fromCharCode(0x2013);
const EM_DASH = String.fromCharCode(0x2014);
const DASH_CLASS = `[${EN_DASH}${EM_DASH}]`;
const JOINERS = [0x200d, 0xfe0f, 0x20e3].map((c) => String.fromCharCode(c)).join("|");
const EMOJI_RE = new RegExp(String.raw`\p{Extended_Pictographic}|[\u{1F1E6}-\u{1F1FF}]|[\u{1F3FB}-\u{1F3FF}]|` + JOINERS, "gu");
const DIGIT_DASH_RE = new RegExp(String.raw`(\d)\s*` + DASH_CLASS + String.raw`\s*(\d)`, "g");
const DASH_RE = new RegExp(String.raw`\s*` + DASH_CLASS + String.raw`\s*`, "g");
const HAS_DASH_RE = new RegExp(DASH_CLASS);

export function stripEmojis(input: string): string {
  return input.replace(EMOJI_RE, "");
}

export function replaceDashes(input: string): string {
  return input.replace(DIGIT_DASH_RE, "$1-$2").replace(DASH_RE, ", ");
}

export function sanitizeGeneratedText(input: string): string {
  return replaceDashes(stripEmojis(input))
    .replace(/ ,/g, ",")
    .replace(/,\s*,/g, ",")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/ +\n/g, "\n")
    .trim();
}

export function containsEmoji(input: string): boolean {
  EMOJI_RE.lastIndex = 0;
  const found = EMOJI_RE.test(input);
  EMOJI_RE.lastIndex = 0;
  return found;
}

export function containsDash(input: string): boolean {
  return HAS_DASH_RE.test(input);
}

/** URL safe slug from French text: "Tondeuse Wahl 5 Stars" -> "tondeuse-wahl-5-stars". */
export function slugify(input: string, maxLength = 60): string {
  return input
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, maxLength)
    .replace(/-+$/g, "");
}

/** Initials for avatars: "Amir Ben Halima" -> "AB". */
export function initials(name: string | null | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const second = parts.length > 1 ? parts[1]![0] : (parts[0]?.[1] ?? "");
  return `${first}${second}`.toUpperCase() || "?";
}

/** Replaces {{variable}} placeholders; unknown variables become empty strings. */
export function renderTemplate(template: string, vars: Record<string, string | number | null | undefined>): string {
  return template.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (_, key: string) => {
    const value = vars[key];
    return value === null || value === undefined ? "" : String(value);
  });
}

export function truncate(input: string, max: number): string {
  if (input.length <= max) return input;
  return `${input.slice(0, Math.max(0, max - 3)).trimEnd()}...`;
}
